import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import MaskedTextInput from 'react-text-mask';
import { CalendarIcon } from '@components/icons/calendar-icon';
import { DatePickerHeader } from '@components/moleculs/date-pickers/date-picker-header';
import { mask } from '@components/moleculs/date-pickers/date-picker/constants';
import type { DatePickerProps } from '@components/moleculs/date-pickers/date-picker/types';
import { DateFnsFormatEnum } from '@models/enums';
import type { ThemeProps } from '@models/types';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import cn from 'classnames';

import { CloseCircleIcon } from '../../../icons';
import { DatePickerContainer, FormHelperText } from '../styles';

export const DatePicker = /* forwardRef<HTMLInputElement, DatePickerProps>( */ (
    props: DatePickerProps /* , ref */,
) => {
    // const currentRef = useRef(ref);
    const {
        containerClassName,
        hint,
        helperText,
        helperTextStatic,
        error,
        inputSize = 'm',
        value,
        onChange,
        showIcon = true,
        isClearable = true,
        placeholder = DateFnsFormatEnum.YEAR_MONTH_DAY,
        startYearRange,
        endYearRange,
        ...rest
    } = props;
    const containerRef = useRef(null);

    const [clearIconContainer, setClearIconContainer] = useState<null | Element>(null);
    const [isOpen, setIsOpen] = useState(false);
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);

    useEffect(() => {
        if (value && !clearIconContainer && containerRef.current) {
            const cleanIcons = (containerRef.current as Element).getElementsByClassName(
                'react-datepicker__close-icon',
            );

            if (cleanIcons[0]) {
                setClearIconContainer(cleanIcons[0]);
            }

            return;
        }
        if (!value && clearIconContainer) {
            setClearIconContainer(null);
        }
    }, [value, clearIconContainer]);

    const handleClearClick = useCallback(
        (e: React.SyntheticEvent) => {
            if (onChange) {
                onChange(null, e);
            }
        },
        [onChange],
    );

    const handleChangeCalendarDate = useCallback(
        (val: Date, event?: React.SyntheticEvent) => {
            if (onChange) {
                onChange(val, event as React.SyntheticEvent);
            }
        },
        [onChange],
    );

    const filterDate = useCallback(
        (date: Date) => (rest.disabledDate ? !rest.disabledDate(date) : false),
        [rest],
    );

    return (
        <DatePickerContainer
            $inputSize={inputSize}
            error={error}
            className={cn(containerClassName, {
                'datepicker-open': isOpen,
                'datepicker-no-icon': !showIcon,
            })}
            ref={containerRef}
        >
            <ReactDatePicker
                {...rest}
                portalId={rest.portalId ?? 'root-date-picker'}
                renderCustomHeader={(params) => (
                    <DatePickerHeader
                        {...params}
                        startYearRange={startYearRange}
                        endYearRange={endYearRange}
                    />
                )}
                popperClassName={
                    rest.disableOpenOnMobile && matchesMobileSm ? 'mobile-hidden' : undefined
                }
                placeholderText={rest.label && !isOpen ? rest.label : placeholder}
                dateFormat={DateFnsFormatEnum.YEAR_MONTH_DAY}
                filterDate={rest.disabledDate ? filterDate : undefined}
                showIcon={showIcon}
                icon={rest.icon ?? <CalendarIcon className='calendar-icon' />}
                selectsMultiple={false}
                selected={value}
                onChange={handleChangeCalendarDate}
                isClearable={isClearable}
                showYearDropdown={rest.showYearDropdown ?? true}
                showMonthDropdown={rest.showMonthDropdown ?? true}
                onCalendarClose={() => {
                    setIsOpen(false);
                    rest.onCalendarClose?.();
                }}
                onCalendarOpen={() => {
                    setIsOpen(true);
                    rest.onCalendarOpen?.();
                }}
                popperPlacement={rest.popperPlacement ?? 'bottom-start'}
                toggleCalendarOnIconClick={rest.toggleCalendarOnIconClick ?? true}
                customInput={
                    rest.customInput ?? (
                        <MaskedTextInput
                            type='text'
                            className='datepicker-input'
                            mask={mask}
                            // render={(textMaskRef, maskedInputProps) => (
                            //     <input
                            //         {...maskedInputProps}
                            //         ref={(node) => {
                            //             textMaskRef(node as HTMLInputElement);
                            //             if (
                            //                 currentRef?.current &&
                            //                 typeof currentRef?.current === 'function'
                            //             ) {
                            //                 currentRef.current?.(node);
                            //             }
                            //         }}
                            //     />
                            // )}
                        />
                    )
                }
            />
            {!!clearIconContainer &&
                createPortal(
                    <CloseCircleIcon onClick={handleClearClick} className='clear-icon' />,
                    clearIconContainer,
                )}
            {(!!hint || !!helperText) && (
                <FormHelperText $helperTextStatic={helperTextStatic}>
                    <span className='helper-container'>
                        {!!hint && <span className='hint'>{hint}</span>}
                        {helperText}
                    </span>
                </FormHelperText>
            )}
        </DatePickerContainer>
    );
};
